import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { FileType } from '../types'
import { getBaseFileNameForComparison } from '../utils/fileTimestamp'

const MAX_FILES = 8

type FileItem = {
  name: string
  type: FileType
  url?: string
  instance: File
  status: 'loading' | 'error' | 'uploaded'
}

type ComposerFileStore = {
  files: FileItem[]
  setFiles: (files: FileItem[]) => void
  upsertFile: (file: FileItem) => void
  uploadFile: (
    file: File,
    uploadFile: (file: File) => Promise<{ fileUrl: string; name: string; type: FileType }>
  ) => void
  deleteFile: (filename: string) => void
}

const createComposerFileStore = (name: string) =>
  create<ComposerFileStore>()(
    persist(
      (set, get) => ({
        files: [],
        setFiles: (files) => set({ files }),
        upsertFile: (incomingFile) =>
          set(({ files }) => {
            const fileExists = files.some((f) => f.name === incomingFile.name)

            const filesArr = fileExists
              ? files.map((file) => (file.name === incomingFile.name ? { ...file, ...incomingFile } : file))
              : [...files, { ...incomingFile }]

            return { files: filesArr }
          }),
        uploadFile: async (file, uploadFile) => {
          const isUpdate = get().files.some((f) => f.name === file.name)
          const hasReachedMaxFiles = get().files.length === MAX_FILES

          if (!isUpdate && hasReachedMaxFiles) return
          const timestamp = new Date().toISOString()

          const baseFileName = getBaseFileNameForComparison(file.name)
          const duplicateCount = get().files.filter((f) => getBaseFileNameForComparison(f.name) === baseFileName).length
          const lastDotIndex = file.name.lastIndexOf('.')
          const hasExtension = lastDotIndex > 0 && lastDotIndex < file.name.length - 1

          const nameWithoutExt = hasExtension ? file.name.substring(0, lastDotIndex) : file.name
          const extension = hasExtension ? file.name.substring(lastDotIndex) : ''
          const duplicateSuffix = duplicateCount > 0 ? ` (${duplicateCount})` : ''
          const fileName = `${timestamp}_${nameWithoutExt}${duplicateSuffix}${extension}`

          const fileWithName = new File([file], fileName, { type: file.type })
          get().upsertFile({
            name: fileName,
            type: 'file',
            instance: fileWithName,
            status: 'loading',
          })
          try {
            const { fileUrl: url, type } = await uploadFile(fileWithName)

            get().upsertFile({
              name: fileName,
              type: type,
              url: url,
              instance: fileWithName,
              status: 'uploaded',
            })
          } catch (error) {
            get().upsertFile({
              name: fileName,
              type: 'file',
              instance: fileWithName,
              status: 'error',
            })
          }
        },
        deleteFile: (filename) =>
          set(({ files }) => {
            return { files: files.filter((file) => file.name != filename) }
          }),
      }),
      {
        name: name,
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )

type BoundComposerFileStore = ReturnType<typeof createComposerFileStore>
class ComposerFileStores {
  private static _instances: { [key: string]: BoundComposerFileStore } = {}
  private constructor() {}

  public static getInstance(name = 'bp-composer-file-store'): BoundComposerFileStore {
    if (!ComposerFileStores._instances[name]) {
      ComposerFileStores._instances[name] = createComposerFileStore(name)
    }
    return ComposerFileStores._instances[name]
  }
}

export const getComposerFileStore = ComposerFileStores.getInstance
